/**********************************

  worldpantry-inventory.js   (TraditionalMedicinals)

  CONSOLIDATED helper (same code as the other stores; only CONFIG differs).
    1. Cookie helpers (used by snippets/worldpantry-fast-shipping-logic.liquid)
    2. Self-heal for stale fast-ship cookies
    3. The single race-proof auto-trigger (zip lookup on load)
    4. Live product-inventory functions

  ZIP ACQUISITION: handled solely by assets/worldpantry-ipzipcode.js
  (the app-proxy file -- key stays server-side). No IPStack key here.

  LIVE INVENTORY: key + endpoint configured below. If any of the three
  config values is blank, every inventory function returns without making
  a request. Copy values EXACTLY as issued (case matters; the inventory
  key is NOT derived from the fast-ship key -- fast-ship is wp_2022_tradmed,
  inventory is WP_2026_TRADMED).

  Shipping lookup/display logic lives in
  snippets/worldpantry-fast-shipping-logic.liquid.

***********************************/

/* ============================================================
   CONFIG -- per-site values.
   ============================================================ */
var WP_INV = {
  // --- live inventory ---
  endpointKey:        'WP_2026_TRADMED',
  inventoryUrlSingle: 'https://shop.worldpantry.com/productinventories/traditionalmedicinals/productinventory.aspx',
  inventoryUrlBatch:  'https://shop.worldpantry.com/productinventories/traditionalmedicinals/productinventories.aspx',

  cookies: {
    zip:       'zipcode',
    fsZip:     'FastShip.ZipCode',
    receiveBy: 'FastShip.ReceiveByDateTime',
    orderBy:   'FastShip.OrderByDateTime',
    calcError: 'FastShip.FastShipCalculationError',
    lookupAt:  'FastShip.LookupTime'        // ms timestamp of the last successful lookup
  },

  // A saved fast-ship result older than this is refetched on the next page
  // load, even if its order-by deadline hasn't passed (catches mid-day
  // changes to cutoffs/carrier schedules). Must match FS.maxAgeMinutes in
  // snippets/worldpantry-fast-shipping-logic.liquid.
  resultMaxAgeMinutes: 60,

  // Auto-trigger hooks (the lookup markup lives in the shared panel snippet)
  zipInputSelector:  '.zip_input',
  zipButtonSelector: '.zip_button',

  // Status display strings
  outOfStockDisplay: 'Usually ships within 1-2 weeks',
  // Plain text only -- the template owns the .cta_order_by arrival line.
  // Do NOT bake a cta_order_by div in here: two elements with that class
  // means two arrival lines.
  inStockDisplay: "In Stock"
};

function wpInventoryEnabled() {
  return !!(WP_INV.endpointKey && WP_INV.inventoryUrlSingle && WP_INV.inventoryUrlBatch);
}

/**********************************
  Cookie functions
***********************************/
// Session cookie (clean form -- no doubled "; ;").
function setSessionCookie(name, value) {
  document.cookie = name + "=" + value + "; path=/";
}

function getSessionCookie(name) {
  // Split FIRST, decode per-value. Decoding the whole cookie string before
  // splitting (the old approach) corrupts the boundaries when any other
  // cookie on the page holds encoded "; " or "=" characters -- TM has
  // several app cookies containing URL-encoded JSON, which made lookups for
  // later cookies fail intermittently.
  var nameKey = name + "=";
  var parts = document.cookie.split('; ');
  for (var i = 0; i < parts.length; i++) {
    if (parts[i].indexOf(nameKey) === 0) {
      return decodeURIComponent(parts[i].substring(nameKey.length));
    }
  }
  return undefined;
}

function deleteCookie(name) {
  document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
}

function getCookie(name) {
  const re = new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)');
  const m = document.cookie.match(re);
  return m ? decodeURIComponent(m[1]) : null;
}

function setCookie(name, value, days) {
  const expires = days ? '; expires=' + new Date(Date.now() + days * 864e5).toUTCString() : '';
  document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; Secure; SameSite=Lax';
}

function isValidZip(str) {
  return /^\d{5}(-\d{4})?$/.test(str || "");
}

/**********************************
  Self-heal (runs immediately, BEFORE the auto-trigger checks for a
  stored result):
   - a stuck error cookie ("No Error" is the API's SUCCESS value, or an
     error with no valid zip beside it)
   - legacy non-ISO date cookies ("June 11" -> Invalid Date in Firefox)
   - dates now in the PAST (a returning visitor's old result). If these
     were left in place, the auto-trigger would see a "stored result" and
     skip the lookup, and the visitor would never get a fresh date.
***********************************/
(function selfHealFastShipCookies() {
  var err = getSessionCookie(WP_INV.cookies.calcError);
  if (err) {
    var zip = getSessionCookie(WP_INV.cookies.fsZip);
    if (err === "No Error" || !isValidZip(zip)) {
      deleteCookie(WP_INV.cookies.calcError);
    }
  }

  var receiveBy = getSessionCookie(WP_INV.cookies.receiveBy);
  var orderBy = getSessionCookie(WP_INV.cookies.orderBy);
  var rd = receiveBy ? new Date(receiveBy) : null;
  var od = orderBy ? new Date(orderBy) : null;

  var today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // The API sends calendar dates as UTC midnight (2026-10-20T00:00:00Z).
  // Reading them with local getFullYear/getMonth/getDate shifts them a day
  // earlier west of GMT, so a date for "tomorrow" reads as already past and
  // the cookies get wiped. Compare UTC components against today's UTC date.
  var todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

  var receivePast = rd && !isNaN(rd) &&
    Date.UTC(rd.getUTCFullYear(), rd.getUTCMonth(), rd.getUTCDate()) < todayUTC;

  var orderByPast = od && !isNaN(od) &&
    Date.UTC(od.getUTCFullYear(), od.getUTCMonth(), od.getUTCDate()) < todayUTC;

  // Too old: no lookup timestamp (saved before this check existed) or older
  // than resultMaxAgeMinutes. Only matters when a result is saved.
  var lookupAt = parseInt(getSessionCookie(WP_INV.cookies.lookupAt), 10);
  var tooOld = !!receiveBy && (isNaN(lookupAt) ||
    (Date.now() - lookupAt) > WP_INV.resultMaxAgeMinutes * 60000);

  if ((rd && isNaN(rd)) || (od && isNaN(od)) || receivePast || orderByPast || tooOld) {
    deleteCookie(WP_INV.cookies.receiveBy);
    deleteCookie(WP_INV.cookies.orderBy);
    deleteCookie(WP_INV.cookies.lookupAt);
  }
})();

/**********************************
  Auto-trigger: if a zipcode cookie exists (or appears -- the proxy sets
  it asynchronously on a first visit), prefill and fire the lookup.
***********************************/
$(document).ready(function () {
  if (window.wpZipAutoTriggered) return;
  window.wpZipAutoTriggered = true;

  var zipPollTicks = 0;
  var zipPollMax = 40;          // ~10s waiting for the async proxy zip
  var clickTries = 0;
  var clickMax = 10;            // retry the click if it raced the handler

  // Done when a result is stored OR any lookup has completed (success or
  // failure -- the shared logic sets wpFsLookupSettled). Without the second
  // check, a zip the API rejects would be re-requested 10 times.
  function lookupDone() {
    return !!getSessionCookie(WP_INV.cookies.receiveBy) || !!window.wpFsLookupSettled;
  }

  function fireAndVerify(zip) {
    if (lookupDone()) return;

    console.log("fast geo zip " + zip + (clickTries ? " (retry " + clickTries + ")" : ""));
    // Fill the zip AND fire 'input' so the button (disabled under 5 chars)
    // enables -- otherwise the programmatic click hits a disabled button.
    $(WP_INV.zipInputSelector).val(zip).trigger('input');
    var $btn = $(WP_INV.zipButtonSelector).first();
    if ($btn.length) $btn.trigger("click");

    clickTries++;
    if (clickTries < clickMax) {
      // Only matters if the click beat the logic's handler binding. A retry
      // while a request is pending is ignored by the logic's in-flight guard.
      setTimeout(function () {
        if (!lookupDone()) fireAndVerify(zip);
      }, 300);
    }
  }

  function waitForZip() {
    if (getSessionCookie(WP_INV.cookies.receiveBy)) {
      console.log("FastShip data already exists, skipping auto lookup");
      return;
    }
    var zip = getCookie(WP_INV.cookies.zip);
    if (isValidZip(zip)) {
      fireAndVerify(zip);
      return;
    }
    zipPollTicks++;
    if (zipPollTicks < zipPollMax) {
      setTimeout(waitForZip, 250);
    } else {
      console.log("no zipcode after polling - manual entry only");
    }
  }

  waitForZip();
});

/**********************************
  Product Inventory functions
  (Inert until WP_INV.endpointKey + URLs are filled in.)
***********************************/
const OutOfStockAlwaysSellStatusDisplay = WP_INV.outOfStockDisplay;
const InStockAlwaysSellStatusDisplay = WP_INV.inStockDisplay;

// Gets inventory for a single product/variant and calls back.
function getProductInventoryDisplay(zip, productId, variantId, setProductInventoryStatus) {
  if (!wpInventoryEnabled()) return;
  var postproductinventory = $.post(WP_INV.inventoryUrlSingle, {
    zip: zip,
    key: WP_INV.endpointKey,
    productid: productId,
    variantid: variantId,
  });
  postproductinventory.done(function (data) {
    if (data) {
      if (data.Status == "OutOfStock" || data.Status == "AlwaysSell") {
        data.StatusDisplay = OutOfStockAlwaysSellStatusDisplay;
      }
      setProductInventoryStatus(data);
    }
  });
}

// Gets WP product/variant numbers for items in the current cart.
function getProductNumbers() {
  if (!wpInventoryEnabled()) return [];
  var productNumbers;
  $.ajax({
    type: "GET",
    url: "/cart?view=wp-productnumbers",
    async: false,
  }).done(function (response) {
    productNumbers = JSON.parse(response);
  });
  return productNumbers;
}

// Gets inventories for a list of product/variant numbers.
function getProductInventories(productNumbers) {
  if (!wpInventoryEnabled() || !productNumbers || !productNumbers.length) return undefined;
  var items = [];
  var productInventories;
  for (var i = 0; i < productNumbers.length; i++) {
    var item = {};
    item["productId"] = productNumbers[i].productNumber;
    item["variantId"] = productNumbers[i].variantNumber;
    items.push(item);
  }

  var shippingInfo = new Object();
  shippingInfo.zip = getCookie(WP_INV.cookies.zip);
  shippingInfo.items = items;
  $.ajax({
    type: "POST",
    url: WP_INV.inventoryUrlBatch,
    data: {
      key: WP_INV.endpointKey,
      shippingInfo: JSON.stringify(shippingInfo),
    },
    async: false,
  }).done(function (data) {
    productInventories = data;
  });
  return productInventories;
}

// ASYNC batch fetch -- preferred (the sync version above blocks the page
// while it waits). Returns a jQuery promise that resolves to the inventory
// array, or undefined on failure. Never rejects.
//   productNumbers: [{ productNumber, variantNumber }, ...]
//   zip: optional; defaults to the shared 'zipcode' cookie.
// If the batch endpoint fails (e.g. 404), falls back to one call per item
// against the single-item endpoint and stitches the results together.
function getProductInventoriesAsync(productNumbers, zip) {
  var d = $.Deferred();
  if (!wpInventoryEnabled() || !productNumbers || !productNumbers.length) {
    d.resolve(undefined);
    return d.promise();
  }
  if (zip === undefined) zip = getCookie(WP_INV.cookies.zip);

  var items = [];
  for (var i = 0; i < productNumbers.length; i++) {
    items.push({
      productId: productNumbers[i].productNumber,
      variantId: productNumbers[i].variantNumber
    });
  }

  $.ajax({
    type: "POST",
    url: WP_INV.inventoryUrlBatch,
    data: {
      key: WP_INV.endpointKey,
      shippingInfo: JSON.stringify({ zip: zip, items: items })
    }
  }).done(function (data) {
    d.resolve(data);
  }).fail(function () {
    // Fallback: single-item endpoint, all requests in parallel.
    var calls = items.map(function (it) {
      return $.post(WP_INV.inventoryUrlSingle, {
        zip: zip,
        key: WP_INV.endpointKey,
        productid: it.productId,
        variantid: it.variantId
      }).then(function (data) {
        if (data) {
          if (!data.ProductId) data.ProductId = it.productId;
          if (!data.VariantId) data.VariantId = it.variantId;
        }
        return data;
      }, function () {
        return $.Deferred().resolve(null).promise();
      });
    });
    $.when.apply($, calls).then(function () {
      var out = [];
      for (var j = 0; j < arguments.length; j++) {
        if (arguments[j]) out.push(arguments[j]);
      }
      d.resolve(out.length ? out : undefined);
    });
  });

  return d.promise();
}

// Whether the cart needs the inventory status display.
function showProductInventoryCartStatusDisplay(productInventories) {
  if (productInventories) {
    for (var i = 0; i < productInventories.length; i++) {
      if (productInventories[i].Status == "AlwaysSell" ||
          productInventories[i].Status == "OutOfStock") {
        return true;
      }
    }
  }
  return false;
}

// Gets a product inventory for the given item ID.
function getProductInventory(id, productNumbers, productInventories) {
  var inventoryStatus;
  if (!productNumbers || !productInventories) return inventoryStatus;
  for (var n = 0; n < productNumbers.length; n++) {
    if (productNumbers[n].id == id) {
      var productNumber = productNumbers[n].productNumber;
      var variantNumber = productNumbers[n].variantNumber;
      for (var i = 0; i < productInventories.length; i++) {
        if (productInventories[i].ProductId &&
            productInventories[i].VariantId &&
            productInventories[i].ProductId.toUpperCase() == productNumber.toUpperCase() &&
            productInventories[i].VariantId.toUpperCase() == variantNumber.toUpperCase()) {
          inventoryStatus = productInventories[i];
          if (inventoryStatus.Status == "OutOfStock" || inventoryStatus.Status == "AlwaysSell") {
            inventoryStatus.StatusDisplay = OutOfStockAlwaysSellStatusDisplay;
          } else {
            inventoryStatus.StatusDisplay = InStockAlwaysSellStatusDisplay;
          }
          break;
        }
      }
      break;
    }
  }
  return inventoryStatus;
}
